import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", tries: 0 });
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const res = await api.get(`/subscription/status/${sessionId}`);
        const s = res.data;
        if (s.payment_status === "paid") {
          setState({ status: "paid", plan: s.plan });
          toast.success("Ödeme alındı, aboneliğiniz aktif");
          return;
        }
        if (s.payment_status === "failed" || s.payment_status === "expired") {
          setState({ status: "failed" });
          return;
        }
      } catch (e) {
        // ignore transient
      }
      if (tries >= 15) {
        setState({ status: "timeout" });
        return;
      }
      setState({ status: "polling", tries });
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return renderStatus(state, nav);
}

export function IyzicoCallback() {
  const [params] = useSearchParams();
  const cid = params.get("conversation_id");
  const [state, setState] = useState({ status: "polling", tries: 0 });
  const nav = useNavigate();

  useEffect(() => {
    if (!cid) {
      setState({ status: "failed" });
      return;
    }
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const res = await api.get(`/subscription/iyzico/status/${cid}`);
        const s = res.data;
        if (s.payment_status === "paid") {
          setState({ status: "paid", plan: s.plan });
          toast.success("Ödeme alındı, aboneliğiniz aktif");
          return;
        }
        if (s.payment_status === "failed") {
          setState({ status: "failed", error: s.error_message });
          return;
        }
      } catch (e) {
        // 404 = unknown conversation_id → terminal failure
        if (e?.response?.status === 404) {
          setState({ status: "failed", error: "İşlem bulunamadı" });
          return;
        }
        // other errors: transient, keep polling
      }
      if (tries >= 20) {
        setState({ status: "timeout" });
        return;
      }
      setState({ status: "polling", tries });
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [cid]);

  return renderStatus(state, nav);
}

function renderStatus(state, nav) {
  return (
    <div className="max-w-md mx-auto py-16 text-center" data-testid="payment-success-page">
      {state.status === "paid" ? (
        <>
          <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
          <h1 className="font-display font-black text-3xl text-slate-900">
            Ödeme başarılı!
          </h1>
          <p className="text-stone-600 mt-3">
            Aboneliğiniz aktif. Panele dönerek galeriyi yönetmeye devam
            edebilirsiniz.
          </p>
          <button
            data-testid="payment-continue"
            onClick={() => nav("/ozet")}
            className="mt-8 h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm inline-flex items-center gap-2"
          >
            Panele dön <ArrowRight className="w-4 h-4" />
          </button>
        </>
      ) : state.status === "failed" ? (
        <>
          <XCircle className="w-16 h-16 text-rose-600 mx-auto mb-4" />
          <h1 className="font-display font-black text-3xl text-slate-900">
            Ödeme tamamlanamadı
          </h1>
          <p className="text-stone-600 mt-3">
            {state.error || "Bir sorun oluştu. Lütfen tekrar deneyin."}
          </p>
          <Link
            to="/abonelik"
            className="mt-8 h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm inline-flex items-center gap-2"
          >
            Abonelik sayfasına dön
          </Link>
        </>
      ) : state.status === "timeout" ? (
        <>
          <div className="text-stone-500">
            Ödeme durumu doğrulanıyor, biraz sürebilir. Sayfayı yenileyebilir
            veya birkaç dakika sonra panele dönebilirsiniz.
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 h-11 px-6 rounded-full border border-stone-300 text-slate-800 hover:bg-stone-50 text-sm font-semibold"
          >
            Tekrar dene
          </button>
        </>
      ) : (
        <>
          <Loader2 className="w-16 h-16 text-orange-600 mx-auto mb-4 animate-spin" />
          <h1 className="font-display font-black text-2xl text-slate-900">
            Ödeme onaylanıyor…
          </h1>
          <p className="text-stone-600 mt-3 text-sm">
            Iyzico ile doğrulama yapılıyor, birkaç saniye sürer.
          </p>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-md mx-auto py-16 text-center" data-testid="payment-cancel-page">
      <XCircle className="w-16 h-16 text-stone-400 mx-auto mb-4" />
      <h1 className="font-display font-black text-3xl text-slate-900">
        Ödeme iptal edildi
      </h1>
      <p className="text-stone-600 mt-3">
        Herhangi bir ücret alınmadı. İstediğinizde tekrar deneyebilirsiniz.
      </p>
      <Link
        to="/abonelik"
        data-testid="payment-back"
        className="mt-8 h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm inline-flex items-center gap-2"
      >
        Abonelik sayfasına dön
      </Link>
    </div>
  );
}
